import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { compact, filter, includes, map, remove } from 'lodash'
import { produce } from 'immer'
import routes from '../routes/routes'
import materialService from '../services/material.service'
import itemService from '../services/item.service'
import itemPrintableService from '../services/itemPrintable.service'
import itemApi, {
  ItemCreateRequestContact, ItemUpdateRequest
} from '../apis/item.api'
import { ContactSiteData } from '../apis/contactSite.api'
import { ContactableType } from '../apis/contact.api'
import { ItemType } from '../apis/ItemType.api'
import { ItemPrintableName } from '../apis/ItemPrintable.api'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import Row from '../components/Row/Row'
import InputText from '../components/InputText'
import PageHeading from '../components/PageHeading'
import ItemContactInput, {
  ItemContactInputData
} from '../partials/ItemContactInput'
import ItemMaterialInput, {
  ItemMaterialInputData
} from '../partials/ItemMaterialInput'
import CheckboxGroup from '../components/CheckboxGroup'
import { i18n } from "../i18n/i18n";

interface RouteState {
  id: number
}

interface Props extends RouteComponentProps<any, any, RouteState> {}

interface State {
  code: string
  types: ItemType[]
  description: string
  value: string
  netWeight: string
  grossWeight: string
  cpp: string
  qpc: string
  cpb: string
  safetyStock: string
  hasActiveInventory: boolean
  labels: ItemPrintableName[]
  materials: ItemMaterialInputData[]
  customers: ItemContactInputData[]
  vendors: ItemContactInputData[]
  contactsAdded: ItemCreateRequestContact[]
  contactsDeleted: ItemCreateRequestContact[]
}

export default class ItemEdit extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      code: '',
      types: [],
      description: '',
      value: '',
      netWeight: '',
      grossWeight: '',
      cpp: '',
      qpc: '',
      cpb: '',
      safetyStock: '',
      hasActiveInventory: true,
      labels: [],
      materials: [],
      vendors: [],
      customers: [],
      contactsAdded: [],
      contactsDeleted: []
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleTypesChange = this.handleTypesChange.bind(this)
    this.handleContactChange = this.handleContactChange.bind(this)
    this.handleMaterialChange = this.handleMaterialChange.bind(this)
    this.handleContactAdd = this.handleContactAdd.bind(this)
    this.handleContactDelete = this.handleContactDelete.bind(this)
    this.handleSaveClick = this.handleSaveClick.bind(this)
  }

  get heading () {
    const { code } = this.state
    const codeText = code ? `: ${code}` : ''
    return `Edit Item${codeText}`
  }

  get id () {
    return this.props.location.state.id
  }

  get vendorInput () {
    return includes(this.state.types, 'receivable') &&
      <ItemContactInput
        type="vendor"
        addeds={this.state.vendors}
        onChange={this.handleContactChange}
        onAdd={this.handleContactAdd}
        onDelete={this.handleContactDelete} />
  }

  get customerInput () {
    return includes(this.state.types, 'production') &&
      <ItemContactInput
        type="customer"
        addeds={this.state.customers}
        onChange={this.handleContactChange}
        onAdd={this.handleContactAdd}
        onDelete={this.handleContactDelete} />
  }

  save () {
    const { description, netWeight, grossWeight, cpp, cpb, qpc, types } = this.state
    const { contactsAdded, contactsDeleted, hasActiveInventory } = this.state
    const { value, safetyStock, labels } = this.state
    const payload: ItemUpdateRequest = {}
    const materials = materialService.payloadData(this.state.materials)
    if (types) payload.types = types
    if (description) payload.description = description
    if (value) payload.value = Number(value)
    if (netWeight) payload.netWeight = Number(netWeight)
    if (grossWeight) payload.grossWeight = Number(grossWeight)
    if (cpp && !hasActiveInventory) payload.cpp = Number(cpp)
    if (qpc && !hasActiveInventory) payload.qpc = Number(qpc)
    if (cpb) payload.ppc = Number(cpb)
    if (safetyStock) payload.safetyStock = Number(safetyStock)
    if (labels) payload.labels = labels
    if (materials) payload.materials = materials
    if (contactsAdded) payload.contactsAdded = contactsAdded
    if (contactsDeleted) payload.contactsDeleted = contactsDeleted
    itemApi.update(this.id, payload, item => {
      if (!item) return
      this.props.history.push(routes.items)
    })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'description') this.setState({ description: value })
    else if (name === 'value') this.setState({ value })
    else if (name === 'netWeight') this.setState({ netWeight: value })
    else if (name === 'grossWeight') this.setState({ grossWeight: value })
    else if (name === 'cpp') this.setState({ cpp: value })
    else if (name === 'qpc') this.setState({ qpc: value })
    else if (name === 'cpb') this.setState({ cpb: value })
    else if (name === 'safetyStock') this.setState({ safetyStock: value })
  }

  handleTypesChange (types: ItemType[]) {
    this.setState({ types })
  }

  handleLabelsChange = (labels: ItemPrintableName[]) => {
    this.setState({ labels })
  }

  handleContactChange (type: ContactableType, addeds: ItemContactInputData[]) {
    if (type === 'customer') this.setState({ customers: addeds })
    else if (type === 'vendor') this.setState({ vendors: addeds })
  }

  handleMaterialChange (materials: ItemMaterialInputData[]) {
    this.setState({ materials })
  }

  handleContactAdd (type: ContactableType, added: ItemContactInputData) {
    const contactsAdded = produce(this.state.contactsAdded, addeds => {
      const { site, partNumber } = added
      remove(addeds, added => added.site === site.id)
      const addedData = { site: site.id, partNumber }
      addeds.push(addedData)
    })
    this.setState({ contactsAdded })
  }

  handleContactDelete (type: ContactableType, deleted: ItemContactInputData) {
    const contactsDeleted = produce(this.state.contactsDeleted, deleteds => {
      const { site, partNumber } = deleted
      remove(deleteds, deleted => deleted.site === site.id)
      const deletedData = { site: site.id, partNumber }
      deleteds.push(deletedData)
    })
    const contactsAdded = produce(this.state.contactsAdded, addeds => {
      const { site } = deleted
      remove(addeds, added => added.site === site.id)
    })
    this.setState({ contactsDeleted, contactsAdded })
  }

  handleSaveClick () {
    this.save()
  }

  readItem () {
    itemApi.read(this.id, item => {
      if (!item) return
      const { code, description, value, netWeight, grossWeight } = item
      const { cpp, qpc, cpb, minimumStock, hasActiveInventory } = item
      const types = map(item.types, type => type.type)
      const labels = itemPrintableService.printableNames(item.printables)
      this.setState({
        code,
        types,
        labels,
        description,
        value: String(value),
        hasActiveInventory,
        netWeight: String(netWeight),
        grossWeight: String(grossWeight),
        cpp: String(cpp),
        qpc: String(qpc),
        cpb: String(cpb),
        safetyStock: String(minimumStock)
      })
      const { contactSites } = item
      if (!contactSites) return
      const contactSitesData = (
        contactSites: ContactSiteData[],
        type: ContactableType
      ) => {
        return filter(contactSites, contactSite => {
          const { contact } = contactSite
          if (!contact) return false
          return contact.contactableType === type
        })
      }
      const contactsData = (contactSites: ContactSiteData[]): ItemContactInputData[] => {
        const contactsData = map(contactSites, contactSite => {
          const { contactSiteItem } = contactSite
          if (
            !contactSite.contact ||
            !contactSite.contact.contactable ||
            !contactSiteItem
          ) return
          return {
            contact: contactSite.contact.contactable,
            site: contactSite,
            partNumber: contactSiteItem && contactSiteItem.partNumber
          }
        })
        return compact(contactsData)
      }
      const customerSites = contactSitesData(contactSites, 'customer')
      const vendorSites = contactSitesData(contactSites, 'vendor')
      const customersData = contactsData(customerSites)
      const vendorsData = contactsData(vendorSites)
      this.setState({ customers: customersData, vendors: vendorsData })
    })
  }

  listMaterials () {
    itemApi.listMaterials(this.id, materials => {
      if (!materials) return
      const materialData: ItemMaterialInputData[] = map(materials, material => {
        return { item: material, quantity: String(material.material.quantity) }
      })
      this.setState({ materials: materialData })
    })
  }

  componentDidMount () {
    this.readItem()
    this.listMaterials()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading
              text={this.heading}
              doc="items-edit-item" />
            <Row column={2}>
              <CheckboxGroup
                checks={itemService.typeChecks}
                checkeds={this.state.types}
                label={i18n("Types")}
                onChange={this.handleTypesChange} />
              <InputText
                name="description"
                value={this.state.description}
                label={i18n("Description")}
                onChange={this.handleInputChange} />
            </Row>
            <Row column={2}>
              <InputText
                name="netWeight"
                value={this.state.netWeight}
                label={i18n("Net weight")}
                onChange={this.handleInputChange} />
              <InputText
                name="grossWeight"
                value={this.state.grossWeight}
                label={i18n("Gross weight")}
                onChange={this.handleInputChange} />
              <InputText
                name="cpp"
                value={this.state.cpp}
                label={i18n("Cases per pallet (CPP)")}
                readonly={this.state.hasActiveInventory}
                onChange={this.handleInputChange} />
              <InputText
                name="qpc"
                value={this.state.qpc}
                label={i18n("Quantity per case (QPC)")}
                readonly={this.state.hasActiveInventory}
                onChange={this.handleInputChange} />
              <InputText
                name="cpb"
                value={this.state.cpb}
                label={i18n("Packs per case (PPC)")}
                onChange={this.handleInputChange} />
              <InputText
                name="safetyStock"
                value={this.state.safetyStock}
                label={i18n("Safety stock")}
                onChange={this.handleInputChange} />
              <InputText
                type="number"
                name="value"
                value={this.state.value}
                label={i18n("Item value")}
                onChange={this.handleInputChange} />
              <CheckboxGroup
                checks={itemPrintableService.labelChecks}
                checkeds={this.state.labels}
                label={i18n("Labels")}
                onChange={this.handleLabelsChange} />
            </Row>
            <ItemMaterialInput
              addeds={this.state.materials}
              onChange={this.handleMaterialChange} />
            {this.vendorInput}
            {this.customerInput}
            <CardButton
              proceedText={i18n("Save")}
              cancelText={i18n("Cancel")}
              onProceedClick={this.handleSaveClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}
